import { type ComputedRef, type WritableComputedRef, computed } from "vue";
import { i18n } from "@/i18n";
import type { MinecraftModFieldsFragment } from "@/graphql";
import { useMinecraftModsStore } from "@/store/minecraftmods";
import type { IUseMods } from "../index";

type MinecraftMod = MinecraftModFieldsFragment;

export interface UseMinecraftMods extends IUseMods<MinecraftMod> {
  minecraftMods: ComputedRef<MinecraftMod[]>;
}

/**
 * Base Minecraft mod interface. Mirrors eternal mods hook but adapted for Minecraft mods.
 * @param installedModNames The current installed mod names (as stored in the Minecraft profile)
 */
export function useMinecraftMods(installedModNames: WritableComputedRef<string[]>) {
  const store = useMinecraftModsStore();
  const t = i18n.global.t;
  const storeTitle = computed(() => t("ngp/mods/dialog/link/minecraftmods" as any));

  const minecraftMods = computed<MinecraftMod[]>(() => store.mods);

  const installedMods = computed<MinecraftMod[]>(() => {
    const names = installedModNames.value ?? [];
    if (!names.length) return [];
    const byName = new Map(minecraftMods.value.map((m) => [m.name, m]));
    return names.map((n) => byName.get(n)).filter((m): m is MinecraftMod => !!m);
  });

  function addMod(mod: MinecraftMod) {
    if (installedModNames.value.includes(mod.name)) return;
    installedModNames.value.push(mod.name);
    installedModNames.value.sort((a, b) => a.localeCompare(b));
  }

  function removeMod(mod: MinecraftMod) {
    const idx = installedModNames.value.indexOf(mod.name);
    if (idx > -1) installedModNames.value.splice(idx, 1);
  }

  function modTags(mod: MinecraftMod) {
    const tags = (mod.tags ?? []).filter((tag): tag is string => !!tag);
    return tags.map((tag) => (tag.endsWith("-framework") ? tag.replace("-framework", "") : tag));
  }

  return { storeTitle, minecraftMods, installedMods, addMod, removeMod, modTags };
}
